// Synthesized practice sounds (F3.6–F3.8): no samples, so nothing to license or download.
// Each function schedules its notes at an exact context time and returns the source nodes, so a
// scheduler can stop them if playback changes before they sound.

/** A short click; accented clicks (beat 1) are higher. */
export function playClick(
  ctx: BaseAudioContext,
  out: AudioNode,
  when: number,
  accent: boolean,
): AudioScheduledSourceNode[] {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.frequency.value = accent ? 1760 : 1175;
  gain.gain.setValueAtTime(0.0001, when);
  gain.gain.exponentialRampToValueAtTime(accent ? 0.9 : 0.6, when + 0.001);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.045);
  osc.connect(gain).connect(out);
  osc.start(when);
  osc.stop(when + 0.05);
  return [osc];
}

const midiToHz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);

/**
 * A plucked, piano-like chord: each note is a triangle wave plus a quieter octave, with a fast
 * attack and an exponential decay. Notes are spread a few milliseconds apart like a soft strum.
 */
export function playChord(
  ctx: BaseAudioContext,
  out: AudioNode,
  when: number,
  midiNotes: number[],
  duration: number,
): AudioScheduledSourceNode[] {
  const nodes: AudioScheduledSourceNode[] = [];
  const level = 0.22 / Math.sqrt(Math.max(1, midiNotes.length));
  const length = Math.max(0.3, Math.min(duration, 2.5));
  midiNotes.forEach((midi, i) => {
    const start = when + i * 0.008;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(level, start + 0.006);
    gain.gain.exponentialRampToValueAtTime(level * 0.35, start + 0.25);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + length);
    gain.connect(out);
    for (const [type, multiple, amount] of [
      ['triangle', 1, 1],
      ['sine', 2, 0.3],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = midiToHz(midi) * multiple;
      const partial = ctx.createGain();
      partial.gain.value = amount;
      osc.connect(partial).connect(gain);
      osc.start(start);
      osc.stop(start + length + 0.05);
      nodes.push(osc);
    }
  });
  return nodes;
}
